import { Badge, SimpleGrid, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconCards } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import type { CSSProperties } from 'react';
import type {
  SpreadPlacedCardView,
  SpreadPositionView,
  SpreadSessionView,
} from '@creativitas/shared';
import { PlayingCard } from '@/components/domain/PlayingCard';
import { cellCentre, isFigureBoard, ownerLabel, placedInPosition } from './spread-table';
import classes from './SpreadCanvas.module.css';

type PresetBoardSpreadProps = {
  session: SpreadSessionView;
  busy: boolean;
  canDraw: boolean;
  onDraw: (positionId: string) => void;
  onInspect: (card: SpreadPlacedCardView) => void;
  /**
   * Рубашка выбранной колоды — для позиций, у которых своей колоды нет.
   * Там, где расклад колоду закрепил, рубашку даёт сама позиция.
   */
  fallbackBackImage: string | null;
  viewerParticipantId?: string;
  viewedParticipantId?: string;
};

const DEFAULT_GRID = 8;

/**
 * Расклад на собственной доске — «Шахматы» (инструкция от 6 сентября).
 *
 * Позиции здесь названы клетками, на которых стоят: «Белая королева», «Чёрный
 * слон». На общем сукне это были бы шесть карт в ряд, у которых просто такие
 * названия, — доска и есть половина смысла расклада.
 *
 * Подписей у карт нет, и это не экономия места: двенадцать кремовых плашек
 * закрыли бы собой доску, ради которой всё и затевалось. Их место — в легенде
 * под доской, ровно как в печатной инструкции: поле с номерами, а под ним
 * нумерованный список. Название позиции при этом никуда не делось — оно в
 * подсказке карты и в её `aria-label`, то есть доступно и мыши, и скринридеру.
 *
 * Стол бывает двух видов, и решают это данные, а не расклад по имени:
 *
 * - **фигуры**, когда у каждой позиции есть своя (`isFigureBoard`). Тогда доска
 *   показывает схему расклада, а карта открывается по нажатию на фигуру и
 *   фигуру собой не заменяет: клетка названа фигурой, и закрыть её картой
 *   значит стереть сам расклад;
 * - **карты**, когда фигур нет. Доска работает как стол, карты ложатся на неё.
 */
export function PresetBoardSpread({
  session,
  busy,
  canDraw,
  onDraw,
  onInspect,
  fallbackBackImage,
  viewerParticipantId,
  viewedParticipantId,
}: PresetBoardSpreadProps) {
  const { spread } = session;
  const grid = spread.boardGrid ?? DEFAULT_GRID;
  const positions = spread.positions;
  const figures = isFigureBoard(spread);

  /* Позиция без клетки садится по своим x/y — доска её не теряет. */
  const seat = (position: SpreadPositionView) => ({
    left: `${position.cell ? cellCentre(position.cell.col, grid) : position.x}%`,
    top: `${position.cell ? cellCentre(position.cell.row, grid) : position.y}%`,
  });

  return (
    <Stack gap="lg">
      <div className={classes.boardViewport}>
        <div
          className={classes.boardCanvas}
          data-testid="preset-board"
          style={
            {
              '--spread-board-image': spread.boardImage ? `url(${spread.boardImage})` : 'none',
              '--spread-board-grid': grid,
            } as CSSProperties
          }
        >
          {positions.map((position) => {
            const placed = placedInPosition(session, position.id, viewedParticipantId);
            const label = `${position.positionIndex + 1}. ${position.title}`;
            const mine = placed && placed.placedByParticipantId === viewerParticipantId;
            const hint = position.hint ?? position.questions[0] ?? null;

            if (figures && position.marker) {
              return (
                <Tooltip
                  key={position.id}
                  label={hint ? `${label} — ${hint}` : label}
                  withArrow
                >
                  <button
                    type="button"
                    className={classes.boardFigure}
                    data-testid={`preset-position-${position.positionIndex}`}
                    data-drawn={placed ? 'true' : 'false'}
                    data-mine={mine ? 'true' : undefined}
                    style={seat(position)}
                    disabled={busy || (!canDraw && !placed)}
                    aria-label={placed ? `Открыть карту: ${label}` : `Вытянуть карту: ${label}`}
                    onClick={
                      placed
                        ? () => onInspect(placed)
                        : canDraw
                          ? () => onDraw(position.id)
                          : undefined
                    }
                  >
                    <Badge className={classes.boardFigureNumber} size="sm" variant="filled" color="teal">
                      {position.positionIndex + 1}
                    </Badge>
                    <img
                      className={classes.boardFigureImage}
                      src={position.marker.image}
                      alt=""
                    />
                    {/* Карта вытянута — фигура остаётся, значок говорит, что за ней есть что открыть. */}
                    {placed ? (
                      <span className={classes.boardFigureMark} aria-hidden="true">
                        <IconCards />
                      </span>
                    ) : null}
                  </button>
                </Tooltip>
              );
            }

            return (
              <div
                key={position.id}
                className={classes.boardPosition}
                data-testid={`preset-position-${position.positionIndex}`}
                data-mine={mine ? 'true' : undefined}
                style={seat(position)}
              >
                <Tooltip label={hint ? `${label} — ${hint}` : label} withArrow>
                  {/*
                    Вытянутая карта опускается на клетку — движение здесь
                    говорит «карта только что пришла», единственное, ради чего
                    §62 вообще разрешает анимацию.
                  */}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={placed?.id ?? `empty-${position.id}`}
                      initial={placed ? { opacity: 0, scale: 0.92, y: -10 } : false}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.28, ease: [0.2, 0.7, 0.3, 1] }}
                    >
                      <PlayingCard
                        frontImage={placed?.card.frontImage ?? null}
                        backImage={position.deckBackImage ?? fallbackBackImage}
                        faceUp={Boolean(placed)}
                        caption={String(position.positionIndex + 1)}
                        label={placed ? label : `Вытянуть карту: ${label}`}
                        width="100%"
                        disabled={busy || (!canDraw && !placed)}
                        onClick={
                          placed
                            ? () => onInspect(placed)
                            : canDraw
                              ? () => onDraw(position.id)
                              : undefined
                        }
                      />
                    </motion.div>
                  </AnimatePresence>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>

      <Stack gap="sm">
        <Title order={4}>Позиции на доске</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs" verticalSpacing="xs">
          {positions.map((position) => {
            const placed = placedInPosition(session, position.id, viewedParticipantId);
            /* Значение позиции: прозой — у «Бизнес-шахмат», вопросом — у «Тени». */
            const meaning = position.hint ?? position.questions[0] ?? null;
            return (
              <div key={position.id} className={classes.boardLegendItem}>
                <Text size="sm" className={classes.boardLegendNumber}>
                  {position.positionIndex + 1}
                </Text>
                {position.marker ? (
                  <img
                    className={classes.boardLegendMarker}
                    src={position.marker.image}
                    alt={position.marker.label}
                  />
                ) : (
                  <span className={classes.boardLegendMarker} />
                )}
                <div>
                  <Text size="sm" fw={600}>
                    {position.title}
                  </Text>
                  {meaning ? (
                    <Text size="xs" c="var(--text-muted)">
                      {meaning}
                    </Text>
                  ) : null}
                  {placed ? (
                    <Text size="xs" c="var(--text-subtle)">
                      {ownerLabel(session, placed.placedByParticipantId, viewerParticipantId)} ·{' '}
                      {placed.deckTitle}
                    </Text>
                  ) : null}
                </div>
              </div>
            );
          })}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
}
